import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthSession, AuthUser, LoginRequest, SignupRequest } from '@streamr/shared';
import { api, apiUrl, refreshAccessToken, setAccessToken } from '../api/client';
import { routes } from '../api/routes';

interface AuthContextValue {
  user: AuthUser | null;
  /** False only until the initial silent refresh has settled. */
  ready: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  signup: (details: SignupRequest) => Promise<void>;
  logout: () => Promise<void>;
  startGoogleLogin: () => void;
  startSynergyLogin: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  /*
   * On a cold load there is no access token in memory -- it is deliberately
   * never persisted -- but there may be a valid httpOnly refresh cookie. One
   * silent refresh turns a returning visitor back into a signed-in user without
   * ever having put a bearer token somewhere an XSS could read it.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const token = await refreshAccessToken();
      if (cancelled) return;
      if (token === null) {
        setReady(true);
        return;
      }
      try {
        const me = await api.get<AuthUser>(routes.auth.me());
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setAccessToken(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const adopt = useCallback(
    (session: AuthSession) => {
      setAccessToken(session.accessToken);
      setUser(session.user);
      // Anything cached while signed out was filtered by a different viewer's
      // visibility rules, so none of it is safe to keep.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const session = await api.post<AuthSession>(routes.auth.login(), credentials, {
        retryOnUnauthorized: false,
      });
      adopt(session);
    },
    [adopt],
  );

  const signup = useCallback(
    async (details: SignupRequest) => {
      const session = await api.post<AuthSession>(routes.auth.signup(), details, {
        retryOnUnauthorized: false,
      });
      adopt(session);
    },
    [adopt],
  );

  const logout = useCallback(async () => {
    try {
      await api.post(routes.auth.logout(), undefined, { retryOnUnauthorized: false });
    } finally {
      // Local state is cleared even if the call failed: the user asked to be
      // signed out, and leaving them looking signed in is the worse outcome.
      setAccessToken(null);
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const startGoogleLogin = useCallback(() => {
    // A full navigation, not fetch: the OAuth dance needs the browser to follow
    // redirects to Google and back, and sets the refresh cookie on return.
    window.location.assign(apiUrl(routes.auth.googleStart()));
  }, []);

  const startSynergyLogin = useCallback(() => {
    // Same reasoning as Google: a full navigation so the browser follows the
    // SynergyAuth redirect chain and comes back with the refresh cookie set.
    window.location.assign(apiUrl(routes.auth.synergyStart()));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setUser(await api.get<AuthUser>(routes.auth.me()));
    } catch {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, ready, login, signup, logout, startGoogleLogin, startSynergyLogin, refreshUser }),
    [user, ready, login, signup, logout, startGoogleLogin, startSynergyLogin, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
};
